import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePatchApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { RequestForm, RequestFormForm, RequestFormSearchForm } from 'entities/request_form'

type RequestFormsResponse = PagingResponse & {
    results: RequestForm[]
}

export function useFetchRequestFormsApi(searchForm?: Form<RequestFormSearchForm>): IndexApiSet<RequestFormsResponse> & { execute: () => void } {
    const apiPath = 'request_forms/'
    const api = useIndexApi<RequestFormsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object.page || 1, perPage: searchForm?.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvRequestFormsApi(searchForm: Form<RequestFormSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'request_forms/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'requestForm', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostRequestFormApi(): ApiSet<BaseResponse> & { execute: (form: Form<RequestFormForm>) => void } {
    const apiSet = usePostApi<BaseResponse, RequestFormForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<RequestFormForm>) => {
        const apiPath = `request_forms/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutRequestFormApi(): ApiSet<BaseResponse> & { execute: (form: RequestFormForm) => void } {
    const apiSet = usePutApi<BaseResponse, RequestFormForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: RequestFormForm) => {
        const apiPath = `request_forms/${form.id}/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutRequestFormsApi(): ApiSet<BaseResponse> & { execute: (form: Form<RequestFormForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, RequestFormForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<RequestFormForm[]>) => {
        const apiPath = `request_forms/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteRequestFormApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `request_forms/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export type RequestFormPrintForm = {
    ids?: string
}

export function usePrintRequestFormApi(): ApiSet<BaseResponse> & { execute: (form: Form<RequestFormPrintForm>) => void } {
    const apiSet = usePatchApi<BaseResponse, RequestFormPrintForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<RequestFormPrintForm>) => {
        const apiPath = `request_forms/print/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}
